import { jsPDF } from 'jspdf';
import { buildDefaultAuditOutputTemplate } from './auditOutputTemplateDefaults';
import { applyTemplateTokens } from './auditOutputTemplateData';
import {
  CERTIFICATE_FIELD_KEYS,
  CERTIFICATE_PAGE_SIZES_MM,
  normalizeCertificateCoordinateLayout,
} from './certificateCoordinateLayout';
import autoTable from 'jspdf-autotable';
import { registerCertificateFonts } from './certificateFonts';

const MM_TO_PT = 2.83465;

const addWrappedText = (doc, text, x, y, maxWidth, lineHeight = 5, options = {}) => {
  const lines = doc.splitTextToSize(String(text || ''), maxWidth);
  if (lines.length > 0) {
    doc.text(lines, x, y, options);
  }
  return y + (lines.length || 1) * lineHeight;
};

const detectImageFormat = (dataUrl = '') => {
  if (String(dataUrl).startsWith('data:image/jpeg') || String(dataUrl).startsWith('data:image/jpg')) {
    return 'JPEG';
  }
  return 'PNG';
};

const normalizeHexColor = (hex = '') => {
  const value = String(hex || '').replace('#', '').trim();
  return /^[0-9a-fA-F]{6}$/.test(value) ? value : '0f766e';
};

const applyHexTextColor = (doc, hex) => {
  const color = normalizeHexColor(hex);
  doc.setTextColor(
    parseInt(color.slice(0, 2), 16),
    parseInt(color.slice(2, 4), 16),
    parseInt(color.slice(4, 6), 16),
  );
};

const applyHexDrawColor = (doc, hex) => {
  const color = normalizeHexColor(hex);
  doc.setDrawColor(
    parseInt(color.slice(0, 2), 16),
    parseInt(color.slice(2, 4), 16),
    parseInt(color.slice(4, 6), 16),
  );
};

const applyHexFillColor = (doc, hex) => {
  const color = normalizeHexColor(hex);
  doc.setFillColor(
    parseInt(color.slice(0, 2), 16),
    parseInt(color.slice(2, 4), 16),
    parseInt(color.slice(4, 6), 16),
  );
};

const getFittedImageDimensions = (doc, dataUrl, maxWidth, maxHeight, preserveAspectRatio = true) => {
  const fallback = {
    width: maxWidth,
    height: maxHeight,
  };

  if (!dataUrl || !maxWidth || !maxHeight || !preserveAspectRatio) {
    return fallback;
  }

  const props = doc.getImageProperties(dataUrl);
  const imageRatio = props.width / props.height;
  if (!Number.isFinite(imageRatio) || imageRatio <= 0) {
    return fallback;
  }

  let width = maxWidth;
  let height = width / imageRatio;

  if (height > maxHeight) {
    height = maxHeight;
    width = height * imageRatio;
  }

  return {
    width,
    height,
  };
};

const addHeader = (doc, template, runtimeData, pageNumber, totalPages) => {
  const { header, logo, page } = template.config;
  const pageWidth = doc.internal.pageSize.getWidth();

  if (page.backgroundImage) {
    try {
      doc.addImage(page.backgroundImage, detectImageFormat(page.backgroundImage), 0, 0, pageWidth, doc.internal.pageSize.getHeight());
    } catch (error) {
      // ignore invalid image payload
    }
  } else if (page.backgroundColor) {
    const rgb = page.backgroundColor.replace('#', '');
    if (rgb.length === 6) {
      doc.setFillColor(
        parseInt(rgb.slice(0, 2), 16),
        parseInt(rgb.slice(2, 4), 16),
        parseInt(rgb.slice(4, 6), 16),
      );
      doc.rect(0, 0, pageWidth, doc.internal.pageSize.getHeight(), 'F');
    }
  }

  if (logo?.enabled && logo?.dataUrl) {
    const fittedLogo = getFittedImageDimensions(
      doc,
      logo.dataUrl,
      logo.width,
      logo.height,
      true,
    );
    doc.addImage(
      logo.dataUrl,
      detectImageFormat(logo.dataUrl),
      logo.x,
      logo.y,
      fittedLogo.width,
      fittedLogo.height,
    );
  }

  if (!header?.enabled) return;

  const title = applyTemplateTokens(header.title, {
    ...runtimeData,
    page: { current: pageNumber, total: totalPages },
  });
  const subtitle = applyTemplateTokens(header.subtitle, {
    ...runtimeData,
    page: { current: pageNumber, total: totalPages },
  });
  const headerX = header.align === 'left' ? template.config.page.paddingLeft : pageWidth / 2;
  const headerOptions = header.align === 'left' ? {} : { align: 'center' };

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  applyHexTextColor(doc, page.textColor || '#1f2937');
  doc.text(title || '', headerX, 18, headerOptions);

  if (subtitle) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(subtitle, headerX, 24, headerOptions);
  }
};

const addFooter = (doc, template, runtimeData, pageNumber, totalPages) => {
  const { footer, page } = template.config;
  if (!footer?.enabled) return;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const leftText = applyTemplateTokens(footer.leftText, {
    ...runtimeData,
    page: { current: pageNumber, total: totalPages },
  });
  const rightText = applyTemplateTokens(footer.rightText, {
    ...runtimeData,
    page: { current: pageNumber, total: totalPages },
  });
  const certificateText = template.outputType === 'certificate'
    ? applyTemplateTokens(footer.certificateText || footer.leftText, {
      ...runtimeData,
      page: { current: pageNumber, total: totalPages },
    })
    : null;

  doc.setDrawColor(203, 213, 225);
  doc.line(page.paddingLeft, pageHeight - 14, pageWidth - page.paddingRight, pageHeight - 14);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(template.outputType === 'certificate' ? 10.2 : 9);

  if (template.outputType === 'certificate') {
    if (certificateText) {
      doc.text(certificateText, page.paddingLeft, pageHeight - 9, {
        maxWidth: pageWidth - page.paddingLeft - page.paddingRight,
        align: 'left',
      });
    }
    if (rightText) {
      doc.text(rightText, pageWidth - page.paddingRight, pageHeight - 9, { align: 'right' });
    }
  } else {
    doc.text(leftText || '', page.paddingLeft, pageHeight - 9);
    doc.text(rightText || '', pageWidth - page.paddingRight, pageHeight - 9, { align: 'right' });
  }
};

const renderISOCertificatePage = (doc, template, runtimeData) => {
  const page = template.config.page;
  const certificateConfig = template.config.certificate;
  const contentWidth = doc.internal.pageSize.getWidth() - page.paddingLeft - page.paddingRight;
  let cursorY = Math.max(42, page.paddingTop + 18);

  const titleText = applyTemplateTokens(certificateConfig.title, runtimeData);
  const subtitleText = applyTemplateTokens(certificateConfig.subtitle, runtimeData);
  const statementText = applyTemplateTokens(certificateConfig.statement, runtimeData);

  if (certificateConfig.standardLogo?.enabled && certificateConfig.standardLogo?.dataUrl) {
    const fittedLogo = getFittedImageDimensions(
      doc,
      certificateConfig.standardLogo.dataUrl,
      certificateConfig.standardLogo.width,
      certificateConfig.standardLogo.height,
      true,
    );
    const logoX = doc.internal.pageSize.getWidth() - page.paddingRight - fittedLogo.width;
    doc.addImage(
      certificateConfig.standardLogo.dataUrl,
      detectImageFormat(certificateConfig.standardLogo.dataUrl),
      logoX,
      certificateConfig.standardLogo.y,
      fittedLogo.width,
      fittedLogo.height,
    );
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(34);
  applyHexTextColor(doc, '#319ea9');
  cursorY = addWrappedText(doc, titleText, page.paddingLeft, cursorY, contentWidth, 10, { align: 'center', maxWidth: contentWidth });

  if (subtitleText) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(17);
    applyHexTextColor(doc, '#000000');
    cursorY = addWrappedText(doc, subtitleText, page.paddingLeft, cursorY + 6, contentWidth, 7, { align: 'center', maxWidth: contentWidth });
  }

  if (statementText) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    applyHexTextColor(doc, '#000000');
    cursorY = addWrappedText(doc, statementText, page.paddingLeft, cursorY + 8, contentWidth, 6, { align: 'center', maxWidth: contentWidth });
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(33);
  cursorY = addWrappedText(doc, applyTemplateTokens(certificateConfig.companyName, runtimeData), page.paddingLeft, cursorY + 12, contentWidth, 8, { align: 'center', maxWidth: contentWidth });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(14);
  cursorY = addWrappedText(doc, applyTemplateTokens(certificateConfig.companyAddress, runtimeData), page.paddingLeft, cursorY + 4, contentWidth, 6, { align: 'center', maxWidth: contentWidth });

  const metaItems = [
    {
      label: applyTemplateTokens(certificateConfig.certificateNumberLabel, runtimeData),
      value: runtimeData?.certificate?.certificateNumber || '-',
    },
    {
      label: applyTemplateTokens(certificateConfig.decisionLabel, runtimeData),
      value: runtimeData?.certificate?.certificationDecision || '-',
    },
  ];
  const metaGap = 6;
  const metaWidth = (contentWidth - metaGap) / 2;
  const metaStartY = cursorY + 8;
  metaItems.forEach((item, index) => {
    const boxX = page.paddingLeft + (index * (metaWidth + metaGap));
    doc.setFillColor(240, 249, 255);
    doc.roundedRect(boxX, metaStartY, metaWidth, 18, 3, 3, 'F');
    doc.setDrawColor(203, 213, 225);
    doc.roundedRect(boxX, metaStartY, metaWidth, 18, 3, 3);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    applyHexTextColor(doc, page.accentColor || '#0f766e');
    doc.text(item.label, boxX + 4, metaStartY + 6);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    applyHexTextColor(doc, page.textColor || '#1f2937');
    doc.text(String(item.value || '-'), boxX + 4, metaStartY + 12);
  });
  cursorY = metaStartY + 24;

  cursorY = addWrappedText(doc, applyTemplateTokens(certificateConfig.standardsIntro, runtimeData), page.paddingLeft, cursorY, contentWidth, 6);
  doc.setFont('helvetica', 'bold');
  cursorY = addWrappedText(doc, applyTemplateTokens(certificateConfig.standardsText, runtimeData), page.paddingLeft, cursorY + 2, contentWidth, 6);

  doc.setFont('myriadpro', 'italic');
  doc.setFontSize(24);
  applyHexTextColor(doc, '#319ea9');
  cursorY += 8;
  doc.text(applyTemplateTokens(certificateConfig.scopeLabel, runtimeData), page.paddingLeft, cursorY);
  doc.setFont('myriadpro', 'normal');
  doc.setFontSize(16);
  applyHexTextColor(doc, '#000000');
  cursorY = addWrappedText(doc, applyTemplateTokens(certificateConfig.scopeText, runtimeData), page.paddingLeft, cursorY + 5, contentWidth, 5);

  const soaText = runtimeData?.certificate?.statementOfApplicability;
  if (soaText) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    cursorY = addWrappedText(
      doc,
      `This is in-accordance with Statement of Applicability Document, Number: ${soaText}`,
      page.paddingLeft,
      cursorY + 4,
      contentWidth,
      5,
    );
  }

  doc.setFont('myriadpro', 'italic');
  doc.setFontSize(24);
  applyHexTextColor(doc, '#319ea9');
  cursorY += 4;
  doc.text(applyTemplateTokens(certificateConfig.verificationLabel, runtimeData), page.paddingLeft, cursorY);
  doc.setFont('myriadpro', 'normal');
  doc.setFontSize(16);
  applyHexTextColor(doc, '#000000');
  cursorY = addWrappedText(doc, applyTemplateTokens(certificateConfig.verificationText, runtimeData), page.paddingLeft, cursorY + 5, contentWidth, 5);

  cursorY += 8;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  (certificateConfig.dates || []).forEach((item, index) => {
    const labelX = index % 2 === 0 ? page.paddingLeft : doc.internal.pageSize.getWidth() / 2 + 4;
    const valueY = cursorY + Math.floor(index / 2) * 14;
    doc.text(applyTemplateTokens(item.label, runtimeData), labelX, valueY);
    doc.setFont('helvetica', 'normal');
    doc.text(applyTemplateTokens(item.value, runtimeData), labelX, valueY + 5);
    doc.setFont('helvetica', 'bold');
  });

  cursorY += Math.ceil((certificateConfig.dates || []).length / 2) * 14 + 8;
  doc.setDrawColor(148, 163, 184);
  doc.line(page.paddingLeft, cursorY, doc.internal.pageSize.getWidth() - page.paddingRight, cursorY);
  cursorY += 8;
  const signatureImage = certificateConfig.signatureImage || {};
  if (signatureImage.enabled && signatureImage.dataUrl) {
    const fittedSignature = getFittedImageDimensions(
      doc,
      signatureImage.dataUrl,
      signatureImage.width,
      signatureImage.height,
      signatureImage.preserveAspectRatio !== false,
    );
    doc.addImage(
      signatureImage.dataUrl,
      detectImageFormat(signatureImage.dataUrl),
      page.paddingLeft,
      cursorY,
      fittedSignature.width,
      fittedSignature.height,
    );
    cursorY += fittedSignature.height + 4;
  }
  doc.setFont('helvetica', 'bold');
  doc.text(applyTemplateTokens(certificateConfig.signatureName, runtimeData), page.paddingLeft, cursorY);
  doc.setFont('helvetica', 'normal');
  doc.text(applyTemplateTokens(certificateConfig.signatureTitle, runtimeData), page.paddingLeft, cursorY + 5);

  if (certificateConfig.accreditationLogos?.iaf?.enabled && certificateConfig.accreditationLogos.iaf.dataUrl) {
    const fittedIAF = getFittedImageDimensions(
      doc,
      certificateConfig.accreditationLogos.iaf.dataUrl,
      certificateConfig.accreditationLogos.iaf.width,
      certificateConfig.accreditationLogos.iaf.height,
      true,
    );
    const logoX = doc.internal.pageSize.getWidth() - page.paddingRight - fittedIAF.width;
    const logoY = cursorY - fittedIAF.height - 4;
    doc.addImage(
      certificateConfig.accreditationLogos.iaf.dataUrl,
      detectImageFormat(certificateConfig.accreditationLogos.iaf.dataUrl),
      logoX,
      logoY,
      fittedIAF.width,
      fittedIAF.height,
    );
  }

  if (certificateConfig.accreditationLogos?.kan?.enabled && certificateConfig.accreditationLogos.kan.dataUrl) {
    const fittedKAN = getFittedImageDimensions(
      doc,
      certificateConfig.accreditationLogos.kan.dataUrl,
      certificateConfig.accreditationLogos.kan.width,
      certificateConfig.accreditationLogos.kan.height,
      true,
    );
    const logoX = doc.internal.pageSize.getWidth() - page.paddingRight - fittedKAN.width;
    const logoY = cursorY - fittedKAN.height - 4 - (certificateConfig.accreditationLogos?.iaf?.enabled ? fittedKAN.height + 6 : 0);
    doc.addImage(
      certificateConfig.accreditationLogos.kan.dataUrl,
      detectImageFormat(certificateConfig.accreditationLogos.kan.dataUrl),
      logoX,
      logoY,
      fittedKAN.width,
      fittedKAN.height,
    );
  }

  if (certificateConfig.remarks) {
    cursorY = addWrappedText(doc, applyTemplateTokens(certificateConfig.remarks, runtimeData), page.paddingLeft, cursorY + 16, contentWidth, 5);
  }
};

const renderReportPage = (doc, template, runtimeData) => {
  const page = template.config.page;
  const contentWidth = doc.internal.pageSize.getWidth() - page.paddingLeft - page.paddingRight;
  let cursorY = Math.max(42, page.paddingTop + 18);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  cursorY = addWrappedText(doc, applyTemplateTokens(template.config.report.title, runtimeData), page.paddingLeft, cursorY, contentWidth, 7);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  cursorY = addWrappedText(doc, applyTemplateTokens(template.config.report.subtitle, runtimeData), page.paddingLeft, cursorY + 2, contentWidth, 5);
  cursorY = addWrappedText(doc, applyTemplateTokens(template.config.report.intro, runtimeData), page.paddingLeft, cursorY + 8, contentWidth, 5);
  cursorY = addWrappedText(doc, applyTemplateTokens(template.config.report.body, runtimeData), page.paddingLeft, cursorY + 4, contentWidth, 5);

  doc.setFont('helvetica', 'bold');
  cursorY += 8;
  doc.text(applyTemplateTokens(template.config.report.conclusionTitle, runtimeData), page.paddingLeft, cursorY);
  doc.setFont('helvetica', 'normal');
  cursorY = addWrappedText(doc, applyTemplateTokens(template.config.report.conclusionText, runtimeData), page.paddingLeft, cursorY + 5, contentWidth, 5);

  doc.setFont('helvetica', 'bold');
  cursorY += 6;
  doc.text(applyTemplateTokens(template.config.report.recommendationTitle, runtimeData), page.paddingLeft, cursorY);
  doc.setFont('helvetica', 'normal');
  addWrappedText(doc, applyTemplateTokens(template.config.report.recommendationText, runtimeData), page.paddingLeft, cursorY + 5, contentWidth, 5);
};

const renderOpinionPage = (doc, template, runtimeData) => {
  const page = template.config.page;
  const contentWidth = doc.internal.pageSize.getWidth() - page.paddingLeft - page.paddingRight;
  let cursorY = Math.max(42, page.paddingTop + 18);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  cursorY = addWrappedText(doc, applyTemplateTokens(template.config.opinion.title, runtimeData), page.paddingLeft, cursorY, contentWidth, 7);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  cursorY = addWrappedText(doc, applyTemplateTokens(template.config.opinion.subtitle, runtimeData), page.paddingLeft, cursorY + 2, contentWidth, 5);
  cursorY = addWrappedText(doc, applyTemplateTokens(template.config.opinion.intro, runtimeData), page.paddingLeft, cursorY + 8, contentWidth, 5);

  doc.setFont('helvetica', 'bold');
  doc.text(applyTemplateTokens(template.config.opinion.decisionLabel, runtimeData), page.paddingLeft, cursorY + 6);
  doc.setFont('helvetica', 'normal');
  cursorY = addWrappedText(doc, applyTemplateTokens(template.config.opinion.decisionValue, runtimeData), page.paddingLeft, cursorY + 12, contentWidth, 5);
  addWrappedText(doc, applyTemplateTokens(template.config.opinion.body, runtimeData), page.paddingLeft, cursorY + 6, contentWidth, 5);
};

const renderSiteAppendix = (doc, template, runtimeData) => {
  const page = template.config.page;
  const contentWidth = doc.internal.pageSize.getWidth() - page.paddingLeft - page.paddingRight;
  let cursorY = Math.max(42, page.paddingTop + 18);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  cursorY = addWrappedText(doc, applyTemplateTokens(template.config.multiSite.title, runtimeData), page.paddingLeft, cursorY, contentWidth, 7);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  cursorY = addWrappedText(doc, applyTemplateTokens(template.config.multiSite.intro, runtimeData), page.paddingLeft, cursorY + 4, contentWidth, 5);

  const includePrimarySite = template.config.multiSite?.includePrimarySiteOnAppendix !== false;
  const annexSites = includePrimarySite
    ? (runtimeData?.certificate?.sites || [])
    : (runtimeData?.certificate?.sites || []).slice(1);
  annexSites.forEach((site, index) => {
    doc.setFont('helvetica', 'bold');
    cursorY = addWrappedText(doc, `${index + 1}. ${site.name}`, page.paddingLeft, cursorY + 6, contentWidth, 5);
    doc.setFont('helvetica', 'normal');
    cursorY = addWrappedText(doc, `Address: ${site.address}`, page.paddingLeft + 4, cursorY + 1, contentWidth - 4, 5);
    cursorY = addWrappedText(doc, `Scope: ${site.scope}`, page.paddingLeft + 4, cursorY + 1, contentWidth - 4, 5);
  });

  addWrappedText(doc, applyTemplateTokens(template.config.multiSite.footerNote, runtimeData), page.paddingLeft, cursorY + 8, contentWidth, 5);
};

const generateISCCPdf = (doc, template, runtimeData) => {
  const page = template.config.page;
  const certificateConfig = template.config.certificate;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // 1. Draw Background
  if (page.backgroundImage) {
    try {
      doc.addImage(page.backgroundImage, detectImageFormat(page.backgroundImage), 0, 0, pageWidth, pageHeight);
    } catch (error) {}
  } else if (page.backgroundColor) {
    const rgb = page.backgroundColor.replace('#', '');
    if (rgb.length === 6) {
      applyHexFillColor(doc, page.backgroundColor);
      doc.rect(0, 0, pageWidth, pageHeight, 'F');
    }
  }

  const isccBlue = page.accentColor || '#065293';
  applyHexTextColor(doc, isccBlue);
  applyHexDrawColor(doc, isccBlue);

  const marginX = page.paddingLeft || 14;
  const marginY = page.paddingTop || 14;
  const rightMargin = page.paddingRight || 14;
  const bottomMargin = page.paddingBottom || 14;
  const contentWidth = pageWidth - marginX - rightMargin;

  // 2. Draw Top Header Box
  doc.setLineWidth(0.8);
  doc.roundedRect(marginX, marginY, contentWidth, 30, 4, 4);

  if (template.config.logo?.enabled && template.config.logo?.dataUrl) {
    const fitted = getFittedImageDimensions(doc, template.config.logo.dataUrl, 30, 24, true);
    doc.addImage(template.config.logo.dataUrl, detectImageFormat(template.config.logo.dataUrl), marginX + 6, marginY + 3, fitted.width, fitted.height);
  }

  if (certificateConfig.standardLogo?.enabled && certificateConfig.standardLogo?.dataUrl) {
    const fitted = getFittedImageDimensions(doc, certificateConfig.standardLogo.dataUrl, 30, 24, true);
    doc.addImage(certificateConfig.standardLogo.dataUrl, detectImageFormat(certificateConfig.standardLogo.dataUrl), pageWidth - rightMargin - 6 - fitted.width, marginY + 3, fitted.width, fitted.height);
  }

  // 3. Draw Main Box
  const mainBoxY = marginY + 34;
  const mainBoxHeight = pageHeight - mainBoxY - bottomMargin;
  doc.roundedRect(marginX, mainBoxY, contentWidth, mainBoxHeight, 8, 8);

  // 4. Draw Content
  let cursorY = mainBoxY + 14;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  applyHexTextColor(doc, page.textColor || '#000000');
  
  const titleText = applyTemplateTokens(certificateConfig.title, runtimeData) || 'Certificate';
  doc.text(titleText, pageWidth / 2, cursorY, { align: 'center' });

  const subtitleText = applyTemplateTokens(certificateConfig.subtitle, runtimeData);
  if (subtitleText) {
    cursorY += 8;
    doc.setFontSize(11);
    const subtitleLines = doc.splitTextToSize(subtitleText, contentWidth - 10);
    doc.text(subtitleLines, pageWidth / 2, cursorY, { align: 'center' });
    cursorY += (subtitleLines.length - 1) * 5;
  }
  
  const statementText = applyTemplateTokens(certificateConfig.statement, runtimeData);
  if (statementText) {
    cursorY += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const statementLines = doc.splitTextToSize(statementText, contentWidth - 10);
    doc.text(statementLines, pageWidth / 2, cursorY, { align: 'center' });
    cursorY += (statementLines.length - 1) * 4;
  }

  cursorY += 12;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  const certNumberLabel = applyTemplateTokens(certificateConfig.certificateNumberLabel, runtimeData) || 'Certificate Number';
  const certNumber = runtimeData?.certificate?.certificateNumber || 'EU-ISCC-Cert-XXX';
  doc.text(`${certNumberLabel}: ${certNumber}`, pageWidth / 2, cursorY, { align: 'center' });

  // Use headers and standard intros for PT CBQA text if available
  const cbqaHeader = applyTemplateTokens(template.config.header.title, runtimeData) || 'PT CBQA GLOBAL INDONESIA';
  cursorY += 12;
  doc.setFontSize(11);
  doc.text(cbqaHeader, pageWidth / 2, cursorY, { align: 'center' });
  
  cursorY += 5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  const cbqaAddress = 'South Quarter Tower A 17th floor Unit B2- C1, Jl. RA. Kartini Kav. 8, Cilandak\\nJakarta Selatan, DKI Jakarta 12430 – INDONESIA';
  doc.text(cbqaAddress, pageWidth / 2, cursorY, { align: 'center' });

  cursorY += 10;
  doc.setFontSize(11);
  doc.text('certifies that', pageWidth / 2, cursorY, { align: 'center' });

  cursorY += 8;
  doc.setFont('helvetica', 'bold');
  const compName = applyTemplateTokens(certificateConfig.companyName, runtimeData);
  doc.text(doc.splitTextToSize(compName, contentWidth - 10), pageWidth / 2, cursorY, { align: 'center' });
  
  cursorY += 5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  const compAddr = applyTemplateTokens(certificateConfig.companyAddress, runtimeData);
  doc.text(doc.splitTextToSize(compAddr, contentWidth - 10), pageWidth / 2, cursorY, { align: 'center' });

  cursorY += 12;
  doc.setFontSize(11);
  
  const stdIntro = applyTemplateTokens(certificateConfig.standardsIntro, runtimeData) || 'complies with the requirements of the certification system';
  doc.text(doc.splitTextToSize(stdIntro, contentWidth - 10), pageWidth / 2, cursorY, { align: 'center' });
  
  cursorY += 5;
  doc.setFont('helvetica', 'bold');
  const stdText = applyTemplateTokens(certificateConfig.standardsText, runtimeData) || 'ISCC EU';
  doc.text(doc.splitTextToSize(stdText, contentWidth - 10), pageWidth / 2, cursorY, { align: 'center' });
  
  cursorY += 5;
  doc.setFont('helvetica', 'normal');
  doc.text('(International Sustainability and Carbon Certification)', pageWidth / 2, cursorY, { align: 'center' });
  cursorY += 5;
  doc.text('and the requirements of the RED III.', pageWidth / 2, cursorY, { align: 'center' });

  cursorY += 10;
  const verifLabel = applyTemplateTokens(certificateConfig.verificationLabel, runtimeData) || 'Place of the audit';
  doc.text(doc.splitTextToSize(verifLabel, contentWidth - 10), pageWidth / 2, cursorY, { align: 'center' });
  
  cursorY += 5;
  const verifText = applyTemplateTokens(certificateConfig.verificationText, runtimeData) || 'n.a';
  doc.text(doc.splitTextToSize(verifText, contentWidth - 10), pageWidth / 2, cursorY, { align: 'center' });

  cursorY += 10;
  doc.setFont('helvetica', 'bold');
  const validFrom = applyTemplateTokens('{{certificate.issueDate}}', runtimeData) || 'DD.MM.YYYY';
  const validTo = applyTemplateTokens('{{certificate.expiredDate}}', runtimeData) || 'DD.MM.YYYY';
  doc.text(`This certificate is valid from ${validFrom} to ${validTo}.`, pageWidth / 2, cursorY, { align: 'center' });

  cursorY += 10;
  doc.setFont('helvetica', 'normal');
  const scopeLabel = applyTemplateTokens(certificateConfig.scopeLabel, runtimeData) || 'The site of the system user is certified as:';
  doc.text(doc.splitTextToSize(scopeLabel, contentWidth - 10), pageWidth / 2, cursorY, { align: 'center' });
  
  cursorY += 8;
  doc.setFont('helvetica', 'bold');
  const scopeText = applyTemplateTokens(certificateConfig.scopeText, runtimeData) || 'Collecting Point';
  doc.text(doc.splitTextToSize(scopeText, contentWidth - 10), pageWidth / 2, cursorY, { align: 'center' });

  // Signature Block
  const sigY = pageHeight - bottomMargin - 46;
  const sigLineY = sigY + 16;
  doc.setLineWidth(0.3);
  doc.setDrawColor(0, 0, 0);
  doc.line(marginX + 10, sigLineY, marginX + 70, sigLineY);
  doc.line(pageWidth - rightMargin - 70, sigLineY, pageWidth - rightMargin - 10, sigLineY);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(`Jakarta, ${validFrom}`, marginX + 40, sigLineY - 2, { align: 'center' });
  doc.text('Place and date of issue', marginX + 40, sigLineY + 5, { align: 'center' });
  
  const sigTitle = applyTemplateTokens(certificateConfig.signatureTitle, runtimeData) || 'Stamp, Signature of issuing party';
  doc.text(sigTitle, pageWidth - rightMargin - 40, sigLineY + 5, { align: 'center' });

  const sigImage = certificateConfig.signatureImage;
  if (sigImage?.enabled && sigImage?.dataUrl) {
    const fittedSig = getFittedImageDimensions(doc, sigImage.dataUrl, 50, 20, true);
    doc.addImage(sigImage.dataUrl, detectImageFormat(sigImage.dataUrl), pageWidth - rightMargin - 40 - (fittedSig.width / 2), sigLineY - 2 - fittedSig.height, fittedSig.width, fittedSig.height);
  }

  // Footer for Page 1
  const footerY = pageHeight - bottomMargin - 10;
  doc.setFontSize(8);
  const leftText = applyTemplateTokens(template.config.footer.leftText, runtimeData) || 'The issuing Certification Body is responsible for the accuracy of this document.';
  doc.text(leftText, pageWidth / 2, footerY, { align: 'center' });
  doc.text('Version / Date: 1 / ' + validFrom, pageWidth / 2, footerY + 4, { align: 'center' });
  doc.text('Page 1 of 2', pageWidth - rightMargin - 2, pageHeight - bottomMargin + 6, { align: 'right' });


  // PAGE 2 (ANNEX)
  doc.addPage();
  
  if (page.backgroundImage) {
    try { doc.addImage(page.backgroundImage, detectImageFormat(page.backgroundImage), 0, 0, pageWidth, pageHeight); } catch (e) {}
  } else if (page.backgroundColor) {
    const rgb = page.backgroundColor.replace('#', '');
    if (rgb.length === 6) {
      applyHexFillColor(doc, page.backgroundColor);
      doc.rect(0, 0, pageWidth, pageHeight, 'F');
    }
  }

  applyHexDrawColor(doc, isccBlue);
  doc.setLineWidth(0.8);
  doc.roundedRect(marginX, marginY, contentWidth, 30, 4, 4);
  if (template.config.logo?.enabled && template.config.logo?.dataUrl) {
    const fitted = getFittedImageDimensions(doc, template.config.logo.dataUrl, 30, 24, true);
    doc.addImage(template.config.logo.dataUrl, detectImageFormat(template.config.logo.dataUrl), marginX + 6, marginY + 3, fitted.width, fitted.height);
  }
  if (certificateConfig.standardLogo?.enabled && certificateConfig.standardLogo?.dataUrl) {
    const fitted = getFittedImageDimensions(doc, certificateConfig.standardLogo.dataUrl, 30, 24, true);
    doc.addImage(certificateConfig.standardLogo.dataUrl, detectImageFormat(certificateConfig.standardLogo.dataUrl), pageWidth - rightMargin - 6 - fitted.width, marginY + 3, fitted.width, fitted.height);
  }

  doc.roundedRect(marginX, mainBoxY, contentWidth, mainBoxHeight, 8, 8);

  let annexY = mainBoxY + 12;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  applyHexTextColor(doc, page.textColor || '#000000');
  doc.text('Annex to the certificate:', pageWidth / 2, annexY, { align: 'center' });
  
  annexY += 8;
  doc.setFontSize(13);
  doc.text('Sustainable materials handled by the certified site', pageWidth / 2, annexY, { align: 'center' });

  annexY += 5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('(This annex is applicable for all scopes except Trader, Trader with storage, Warehouse, Logistic centre,\\nMTBE and ETBE plant)', pageWidth / 2, annexY, { align: 'center' });

  annexY += 12;
  doc.setFontSize(11);
  doc.text('This annex is only valid in connection with the certificate:', pageWidth / 2, annexY, { align: 'center' });
  annexY += 5;
  doc.setFont('helvetica', 'bold');
  doc.text(`${certNumber} issued on ${validFrom}`, pageWidth / 2, annexY, { align: 'center' });

  annexY += 8;

  const tableData = normalizeIsccMaterialRows(runtimeData?.certificate?.isccMaterials, runtimeData) || [
    ['Used cooking oil (UCO)\\nentirely of veg. origin', 'Collecting Point', 'Used cooking oil (UCO)\\nentirely of veg. origin', 'Collecting Point', '1', 'A', '-'],
    ['Animal fats from\\nrendering (category 3)', 'Collecting Point', 'Animal fats from\\nrendering (category 3)', 'Collecting Point', '2', 'A', '-'],
  ];

  autoTable(doc, {
    startY: annexY,
    margin: { left: marginX, right: rightMargin },
    head: [
      [
        { content: 'Input materials', colSpan: 2, styles: { halign: 'center' } },
        { content: 'Output material', colSpan: 2, styles: { halign: 'center' } },
        { content: 'GHG\\noption', rowSpan: 2, styles: { halign: 'center', valign: 'middle' } },
        { content: 'Criteria of\\nraw material\\ncertification', rowSpan: 2, styles: { halign: 'center', valign: 'middle' } },
        { content: 'Add-ons\\n(volunt-ary)', rowSpan: 2, styles: { halign: 'center', valign: 'middle' } },
      ],
      [
        { content: 'Material', styles: { halign: 'center' } },
        { content: 'Scope*', styles: { halign: 'center' } },
        { content: 'Material', styles: { halign: 'center' } },
        { content: 'Scope*', styles: { halign: 'center' } },
      ]
    ],
    body: tableData,
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 2, textColor: [0, 0, 0], lineColor: [0, 0, 0], lineWidth: 0.2 },
    headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold' },
    columnStyles: {
      0: { cellWidth: 35, halign: 'center', valign: 'middle' },
      1: { cellWidth: 25, halign: 'center', valign: 'middle' },
      2: { cellWidth: 35, halign: 'center', valign: 'middle' },
      3: { cellWidth: 25, halign: 'center', valign: 'middle' },
      4: { halign: 'center', valign: 'middle' },
      5: { halign: 'center', valign: 'middle' },
      6: { halign: 'center', valign: 'middle' },
    },
  });

  const finalY = doc.lastAutoTable.finalY + 4;
  doc.setFontSize(7);
  doc.text('* Please indicate which scope(s) handle the material in case the certificate covers more than one scope.', marginX + 2, finalY);

  doc.setFontSize(8);
  doc.text(leftText, pageWidth / 2, footerY, { align: 'center' });
  doc.text('Version / Date: 1 / ' + validFrom, pageWidth / 2, footerY + 4, { align: 'center' });
  doc.text('Page 2 of 2', pageWidth - rightMargin - 2, pageHeight - bottomMargin + 6, { align: 'right' });
};

const normalizeIsccMaterialRows = (rows, runtimeData) => {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  return rows.map((row) => {
    if (Array.isArray(row)) return row;
    return [
      row.inputMaterial || row.input_material || '-',
      row.inputScope || row.input_scope || runtimeData?.certificate?.scope || '-',
      row.outputMaterial || row.output_material || '-',
      row.outputScope || row.output_scope || runtimeData?.certificate?.scope || '-',
      row.ghgOption || row.ghg_option || runtimeData?.certificate?.ghgEmissionReductions || '-',
      row.rawMaterialCriteria || row.raw_material_criteria || '-',
      row.addOns || row.add_ons || '-',
    ];
  });
};

const certificateCoordinateFieldValue = (key, certificateConfig, runtimeData) => {
  if (key === 'certificateNumber') return runtimeData?.certificate?.certificateNumber || '-';
  if (key === 'datesBlock') {
    return (certificateConfig.dates || [])
      .map((item) => `${applyTemplateTokens(item.label, runtimeData)}: ${applyTemplateTokens(item.value, runtimeData)}`)
      .join('\n');
  }
  if (key === 'signatureBlock') {
    return [
      applyTemplateTokens(certificateConfig.signatureName, runtimeData),
      applyTemplateTokens(certificateConfig.signatureTitle, runtimeData),
    ].filter(Boolean).join('\n');
  }
  if (key === 'remarks') {
    return applyTemplateTokens(certificateConfig.remarks || runtimeData?.certificate?.footerLeft || '', runtimeData);
  }
  return applyTemplateTokens(certificateConfig[key], runtimeData);
};

const drawCoordinateField = (doc, field, value) => {
  if (!value) return;
  const fontSize = field.fontSize || 9;
  doc.setFontSize(fontSize);
  const lines = doc.splitTextToSize(String(value), field.width);
  const lineHeight = Math.max(fontSize * 0.36, 3.2);
  const maxLines = Math.max(1, Math.floor(field.height / lineHeight));
  doc.text(lines.slice(0, maxLines), field.x, field.y + (fontSize * 0.35), {
    align: field.align || 'left',
    maxWidth: field.width,
    lineHeightFactor: 1.15,
  });
};

const renderCertificateCoordinateLayer = (doc, template, runtimeData) => {
  const layout = normalizeCertificateCoordinateLayout(template.config.certificate.coordinateLayout);
  if (!layout.enabled) return;

  doc.setFont('myriadpro', 'normal');
  applyHexTextColor(doc, template.config.page.textColor || '#1f2937');

  CERTIFICATE_FIELD_KEYS.forEach(({ key }) => {
    const field = layout.fields[key];
    const value = certificateCoordinateFieldValue(key, template.config.certificate, runtimeData);
    drawCoordinateField(doc, field, value);
  });
};

export const generateAuditOutputPdf = async (templateInput, runtimeData) => {
  const template = buildDefaultAuditOutputTemplate(templateInput);
  const isISCC = (template.standards || []).some(s => String(s).toUpperCase().includes('ISCC'));
  const coordinateLayoutEnabled = normalizeCertificateCoordinateLayout(template.config.certificate.coordinateLayout).enabled;
  const sitePages = template.siteMode === 'multi-site' && (runtimeData?.certificate?.sites || []).length > 1 ? 1 : 0;
  const coordinateLayout = normalizeCertificateCoordinateLayout(template.config.certificate.coordinateLayout);
  const pageSize = coordinateLayoutEnabled
    ? CERTIFICATE_PAGE_SIZES_MM[coordinateLayout.pageSize] || CERTIFICATE_PAGE_SIZES_MM.a3
    : CERTIFICATE_PAGE_SIZES_MM.a4;
  const doc = new jsPDF({
    unit: 'mm',
    format: [pageSize.width, pageSize.height],
    orientation: 'portrait',
  });
  await registerCertificateFonts(doc);

  if (isISCC && template.outputType === 'certificate' && !coordinateLayoutEnabled) {
    generateISCCPdf(doc, template, runtimeData);
    if (sitePages > 0) {
      doc.addPage();
      const totalPages = 2 + sitePages;
      addHeader(doc, template, runtimeData, 3, totalPages);
      renderSiteAppendix(doc, template, runtimeData);
      addFooter(doc, template, runtimeData, 3, totalPages);
    }
    return doc;
  }

  const totalPages = 1 + sitePages;
  addHeader(doc, template, runtimeData, 1, totalPages);

  if (template.outputType === 'certificate') {
    if (coordinateLayoutEnabled) {
      renderCertificateCoordinateLayer(doc, template, runtimeData);
    } else {
      renderISOCertificatePage(doc, template, runtimeData);
    }
  } else if (template.outputType === 'report') {
    renderReportPage(doc, template, runtimeData);
  } else {
    renderOpinionPage(doc, template, runtimeData);
  }
  addFooter(doc, template, runtimeData, 1, totalPages);

  if (sitePages > 0) {
    doc.addPage();
    addHeader(doc, template, runtimeData, 2, totalPages);
    renderSiteAppendix(doc, template, runtimeData);
    addFooter(doc, template, runtimeData, 2, totalPages);
  }

  return doc;
};

export const buildAuditOutputPdfPreview = async (template, runtimeData) => {
  const doc = await generateAuditOutputPdf(template, runtimeData);
  const blob = doc.output('blob');
  const url = URL.createObjectURL(blob);
  return {
    doc,
    blob,
    url,
  };
};

export const downloadAuditOutputPdf = async (template, runtimeData, fileName = 'audit-output.pdf') => {
  const doc = await generateAuditOutputPdf(template, runtimeData);
  doc.save(fileName);
};

export const pagePreviewStyle = (template) => {
  const merged = buildDefaultAuditOutputTemplate(template);
  const coordinateLayout = normalizeCertificateCoordinateLayout(merged.config.certificate.coordinateLayout);
  const pageSize = coordinateLayout.enabled
    ? CERTIFICATE_PAGE_SIZES_MM[coordinateLayout.pageSize] || CERTIFICATE_PAGE_SIZES_MM.a3
    : CERTIFICATE_PAGE_SIZES_MM.a4;
  return {
    width: `${pageSize.width * MM_TO_PT / 3.78}px`,
    minHeight: `${pageSize.height * MM_TO_PT / 3.78}px`,
    backgroundColor: merged.config.page.backgroundColor,
    backgroundImage: merged.config.page.backgroundImage ? `url(${merged.config.page.backgroundImage})` : 'none',
    backgroundSize: 'cover',
    color: merged.config.page.textColor,
    paddingTop: `${merged.config.page.paddingTop * MM_TO_PT / 3.78}px`,
    paddingRight: `${merged.config.page.paddingRight * MM_TO_PT / 3.78}px`,
    paddingBottom: `${merged.config.page.paddingBottom * MM_TO_PT / 3.78}px`,
    paddingLeft: `${merged.config.page.paddingLeft * MM_TO_PT / 3.78}px`,
  };
};
